import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Hub, Post } from '@/types'

import Link from 'next/link'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { HubIcon } from '@/components/generals/hub-icon'
import { JsonLd } from '@/components/generals/json-ld'
import { FeaturedPost } from '@/components/blog/featured-post'
import { HubPostsGrid } from '@/components/blog/hub-posts-grid'

import { cn } from '@/lib/utils'
import { HUB_LINKS } from '@/constants/navigation'
import {
    countPosts,
    getFeaturedPost,
    getHubBySlug,
    getPosts
} from '@/lib/db'
import { breadcrumbSchema, graph, hubCollectionSchema } from '@/lib/structured-data'

interface HubPageProps {
    hub: Hub
    featured: Post | null
    initialPosts: Post[]
    total: number
}

const HubPage: FC<HubPageProps> = ({ hub, featured, initialPosts, total }): ReactNode => {
    const gridTotal = featured ? Math.max(total - 1, 0) : total
    const collectionPosts = featured ? [featured, ...initialPosts] : initialPosts

    return (
        <Layout>
            <Seo
                title={hub.name}
                description={hub.description}
                path={`/${hub.slug}`}
                ogImage={hub.coverImage ?? undefined}
                imageAlt={hub.name}
            />

            <JsonLd
                data={graph([
                    breadcrumbSchema([
                        { name: 'Home', path: '/' },
                        { name: hub.name }
                    ]),
                    hubCollectionSchema(hub, collectionPosts)
                ])}
            />

            {/* Hub header */}
            <section className='border-b border-border'>
                <Container className='py-10 lg:py-14'>
                    <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: hub.name }]} />

                    <div className='mt-8 grid items-center gap-10 lg:grid-cols-2'>
                        <div>
                            <div className='flex items-center gap-3'>
                                <span className='grid size-11 place-items-center rounded-lg bg-primary/10 text-primary'>
                                    <HubIcon name={hub.icon} className='size-6' />
                                </span>
                                <span className='text-xs font-semibold uppercase tracking-wider text-primary'>
                                    Topic hub
                                </span>
                            </div>

                            <h1 className='mt-5 font-heading text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl'>
                                {hub.title}
                            </h1>

                            <p className='mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground'>
                                {hub.description}
                            </p>

                            <p className='mt-6 text-sm font-medium text-muted-foreground'>
                                {total} {total === 1 ? 'article' : 'articles'} in this hub
                            </p>
                        </div>

                        <div className='overflow-hidden rounded-2xl border border-border'>
                            <img
                                src={hub.coverImage ?? '/images/hero-smile.jpg'}
                                alt={hub.name}
                                loading='eager'
                                decoding='async'
                                className='aspect-[16/10] w-full object-cover'
                            />
                        </div>
                    </div>
                </Container>
            </section>

            {/* Hub sub-navigation */}
            <div className='border-b border-border bg-secondary/40'>
                <Container className='flex flex-wrap gap-2 py-4'>
                    {HUB_LINKS.map(link => {
                        const active = link.href === `/${hub.slug}`

                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                                    active
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-card text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {link.label}
                            </Link>
                        )
                    })}
                </Container>
            </div>

            <Container className='py-14 lg:py-16'>
                {featured && (
                    <div className='mb-12'>
                        <FeaturedPost post={featured} eyebrow='Start here' />
                    </div>
                )}

                <h2 className='mb-8 font-heading text-2xl font-bold tracking-tight text-foreground'>
                    {featured ? 'More articles' : 'Articles'}
                </h2>

                {initialPosts.length > 0 ? (
                    <HubPostsGrid
                        hubSlug={hub.slug}
                        initialPosts={initialPosts}
                        total={gridTotal}
                        excludeSlug={featured?.slug}
                    />
                ) : (
                    <p className='rounded-lg border border-dashed border-border bg-secondary/40 p-10 text-center text-sm text-muted-foreground'>
                        New guides for this hub are on the way. Check back soon.
                    </p>
                )}
            </Container>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HubPageProps> = async context => {
    const hubSlug = context.params?.hub as string
    const hub = await getHubBySlug(hubSlug)

    if (!hub) {
        return { notFound: true }
    }

    const featured = await getFeaturedPost(hubSlug)
    const [initialPosts, total] = await Promise.all([
        getPosts({ hub: hubSlug, excludeSlug: featured?.slug, limit: 9 }),
        countPosts({ hub: hubSlug })
    ])

    return {
        props: {
            hub,
            featured: featured ?? null,
            initialPosts,
            total
        }
    }
}

export default HubPage
